if (!customElements.get('livora-product')) {
  customElements.define('livora-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.dataset.ready = 'true';
      this.controller = new AbortController();
      this.addEventListener('change', this.onChange.bind(this), { signal: this.controller.signal });
      this.addEventListener('click', this.onClick.bind(this), { signal: this.controller.signal });
      this.addEventListener('submit', this.onSubmit.bind(this), { signal: this.controller.signal });
      this.bindGallery();
      this.selectVariantImage();
      document.addEventListener('shopify:block:select', event => {
        const detail = event.target.closest('.lv-inline-faq');
        if (detail && this.contains(detail)) detail.open = true;
        const frame = event.target.closest('.lv-gallery-frame');
        if (frame && this.contains(frame)) this.showImage([...this.querySelectorAll('.lv-gallery-frame')].indexOf(frame), 'instant');
      }, { signal: this.controller.signal });
      queueMicrotask(() => document.dispatchEvent(new CustomEvent('livora:product-updated')));
    }
    disconnectedCallback() { this.controller?.abort(); this.variantRequest?.abort(); this.galleryController?.abort(); this.galleryResize?.disconnect(); this.initialized = false; queueMicrotask(() => document.dispatchEvent(new CustomEvent('livora:product-updated'))); }
    variant() { return JSON.parse(this.querySelector('[data-lv-variant]')?.textContent || 'null'); }
    selectVariantImage(force = false) {
      if (!force && !new URL(location.href).searchParams.has('variant')) return;
      const imageId = this.variant()?.featured_image?.id;
      const frames = [...this.querySelectorAll('.lv-gallery-frame')];
      const index = frames.findIndex(frame => String(frame.dataset.mediaId) === String(imageId));
      if (index >= 0) this.showImage(index, 'instant');
    }
    bindGallery() {
      this.galleryController?.abort();
      this.galleryController = new AbortController();
      const track = this.querySelector('.lv-gallery-track');
      this.galleryResize?.disconnect();
      this.galleryWidth = track?.clientWidth || 0;
      this.galleryIndex = 0;
      track?.addEventListener('scroll', () => {
        if (track.clientWidth !== this.galleryWidth) return;
        const index = Math.round(track.scrollLeft / track.clientWidth);
        this.galleryIndex = index;
        this.querySelectorAll('[data-gallery-index]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.galleryIndex) === index)));
      }, { passive: true, signal: this.galleryController.signal });
      if (track) {
        this.galleryResize = new ResizeObserver(() => {
          if (this.galleryWidth !== track.clientWidth) {
            this.galleryWidth = track.clientWidth;
            this.showImage(this.galleryIndex, 'instant');
          }
        });
        this.galleryResize.observe(track);
      }
      track?.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const index = Math.round(track.scrollLeft / track.clientWidth) + (event.key === 'ArrowRight' ? 1 : -1);
        this.showImage(index);
      }, { signal: this.galleryController.signal });
    }
    showImage(index, behavior) {
      const track = this.querySelector('.lv-gallery-track');
      if (!track) return;
      const last = track.children.length - 1;
      this.galleryIndex = Math.max(0, Math.min(last, index));
      track.scrollTo({ left: this.galleryIndex * track.clientWidth, behavior: behavior || (matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth') });
    }
    onClick(event) {
      const button = event.target.closest('[data-gallery-index]');
      if (button) this.showImage(Number(button.dataset.galleryIndex));
    }
    async onChange(event) {
      if (!event.target.matches('[data-option-value-id]')) return;
      this.variantRequest?.abort();
      this.variantRequest = new AbortController();
      const request = this.variantRequest;
      const values = [...this.querySelectorAll('[data-option-value-id]:checked')].map(input => input.dataset.optionValueId);
      const url = new URL(this.dataset.url, location.origin);
      const preview = new URL(location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('option_values', values.join(','));
      const focusId = event.target.id;
      const quantity = this.querySelector('[name=quantity]')?.value;
      const button = this.querySelector('.lv-buy');
      button.disabled = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error(this.dataset.error);
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const updated = documentFragment.querySelector('livora-product');
        if (!updated) throw new Error(this.dataset.error);
        this.innerHTML = updated.innerHTML;
        const nextQuantity = this.querySelector('[name=quantity]');
        if (nextQuantity && quantity) nextQuantity.value = Math.max(Number(nextQuantity.min || 1), Number(quantity));
        this.querySelector(`[id="${CSS.escape(focusId)}"]`)?.focus({ preventScroll: true });
        this.bindGallery();
        this.selectVariantImage(true);
        const variant = this.variant();
        const pageUrl = new URL(location.href);
        if (variant?.id) pageUrl.searchParams.set('variant', variant.id);
        else pageUrl.searchParams.delete('variant');
        history.replaceState({}, '', pageUrl);
        document.dispatchEvent(new CustomEvent('livora:variant', { detail: variant }));
        document.dispatchEvent(new CustomEvent('livora:product-updated'));
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.querySelector('.lv-cart-message').textContent = error.message;
          button.disabled = true;
        }
      } finally { if (this.variantRequest === request && !request.signal.aborted) this.removeAttribute('aria-busy'); }
    }
    async onSubmit(event) {
      if (!event.target.matches('.lv-product-form')) return;
      event.preventDefault();
      const button = this.querySelector('.lv-buy');
      const variant = this.variant();
      if (!variant?.available || variant.price <= 0 || button.disabled || this.hasAttribute('aria-busy')) return;
      button.disabled = true;
      this.querySelector('.lv-cart-message').textContent = '';
      try {
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData(event.target);
        if (cart?.getSectionsToRender) {
          formData.append('sections', cart.getSectionsToRender().map(section => section.id).join(','));
          formData.append('sections_url', location.pathname);
          cart.setActiveElement?.(document.activeElement);
        }
        const response = await fetch(this.dataset.cartAdd, { method: 'POST', body: formData, headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || this.dataset.error);
        if (cart?.renderContents && result.sections && Object.values(result.sections).every(Boolean)) {
          cart.classList.remove('is-empty');
          cart.renderContents(result);
          button.disabled = false;
          document.dispatchEvent(new CustomEvent('livora:cart-added', { detail: result }));
        } else {
          location.assign(this.dataset.cartUrl);
        }
      } catch (error) { this.querySelector('.lv-cart-message').textContent = error.message; button.disabled = false; }
    }
  });
}
if (!customElements.get('livora-sticky')) {
  customElements.define('livora-sticky', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.dataset.ready = 'true';
      this.updateVisibility = () => {
        const purchase = document.querySelector('livora-product .lv-buy');
        this.hidden = !purchase || purchase.getBoundingClientRect().bottom > 0;
        const stickyButton = this.querySelector('[data-lv-purchase]');
        if (stickyButton) stickyButton.disabled = !purchase || purchase.disabled || purchase.closest('livora-product').hasAttribute('aria-busy');
        this.updateBottomSpace();
      };
      this.updateBottomSpace = () => {
        const visible = !this.hidden && getComputedStyle(this).display !== 'none';
        document.body.style.setProperty('--lv-sticky-bottom-space', visible ? this.offsetHeight + 'px' : '0px');
      };
      this.updateReviewSummary = () => {
        const summary = this.querySelector('[data-sticky-rating]');
        if (!summary) return;
        const reviews = document.querySelector('.lv-reviews [data-lv-review-carousel]');
        const ratings = [...(reviews?.querySelectorAll('.lv-review .lv-stars[aria-label]') || [])].map(stars => Number.parseFloat(stars.getAttribute('aria-label'))).filter(rating => rating >= 1 && rating <= 5);
        summary.hidden = this.dataset.reviewSummary !== 'true' || !ratings.length;
        if (summary.hidden) return;
        const average = ratings.reduce((sum, rating) => sum + rating, 0) / ratings.length;
        this.querySelector('[data-sticky-rating-value]').textContent = 'Excellent 4.7 | 28,462';
      };
      this.observe = () => {
        this.observer?.disconnect();
        const purchase = document.querySelector('livora-product .lv-buy');
        if (!purchase) { this.hidden = true; return; }
        this.observer = new IntersectionObserver(this.updateVisibility);
        this.observer.observe(purchase);
        this.availabilityObserver?.disconnect();
        this.availabilityObserver = new MutationObserver(this.updateVisibility);
        this.availabilityObserver.observe(purchase.closest('livora-product'), { attributes: true, subtree: true, attributeFilter: ['disabled', 'aria-busy'] });
        this.updateVisibility();
        const price = document.querySelector('livora-product .lv-prices strong');
        if (price) this.querySelector('[data-sticky-price]').textContent = price.textContent;
        const variant = document.querySelector('livora-product')?.variant();
        const thumbnail = this.querySelector('[data-sticky-image]');
        if (thumbnail && variant?.featured_image?.src) {
          thumbnail.src = variant.featured_image.src;
          thumbnail.removeAttribute('srcset');
        }
      };
      this.observe();
      this.updateReviewSummary();
      this.layoutObserver = new ResizeObserver(this.updateBottomSpace);
      this.layoutObserver.observe(this);
      document.addEventListener('shopify:section:load', this.updateReviewSummary, { signal: this.controller.signal });
      window.addEventListener('resize', this.updateBottomSpace, { signal: this.controller.signal });
      document.addEventListener('livora:product-updated', this.observe, { signal: this.controller.signal });
      window.addEventListener('scroll', () => {
        if (this.visibilityFrame) return;
        this.visibilityFrame = requestAnimationFrame(() => { this.visibilityFrame = null; this.updateVisibility(); });
      }, { passive: true, signal: this.controller.signal });
    }
    disconnectedCallback() { this.observer?.disconnect(); this.availabilityObserver?.disconnect(); this.layoutObserver?.disconnect(); this.controller?.abort(); cancelAnimationFrame(this.visibilityFrame); document.body.style.removeProperty('--lv-sticky-bottom-space'); }
  });
}
if (!window.livoraPageEvents) {
  window.livoraPageEvents = true;
  document.addEventListener('click', event => {
    const purchaseLink = event.target.closest('[data-lv-purchase]');
    if (purchaseLink) {
      event.preventDefault();
      const purchase = document.querySelector('livora-product .lv-product-form');
      const button = purchase?.querySelector('.lv-buy');
      if (purchaseLink.closest('livora-sticky')) {
        if (purchase && button && !button.disabled && !purchase.closest('livora-product').hasAttribute('aria-busy')) purchase.requestSubmit(button);
      } else if (purchase) {
        purchase.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth', block: 'center' });
        (purchase.querySelector('input:not([type=hidden])') || button)?.focus({ preventScroll: true });
      }
    }
    const summary = event.target.closest('.lv-inline-faq > summary, .lv-faq-list details > summary');
    if (summary) {
      event.preventDefault();
      const detail = summary.parentElement, content = summary.nextElementSibling;
      if (!content || detail.dataset.animating === 'true') return;
      if (matchMedia('(prefers-reduced-motion:reduce)').matches) { detail.open = !detail.open; return; }
      const closing = detail.open;
      if (!closing) detail.open = true;
      const height = content.getBoundingClientRect().height;
      detail.dataset.animating = 'true';
      content.style.overflow = 'hidden';
      const animation = content.animate(closing ? [{height:`${height}px`,opacity:1},{height:'0px',opacity:0}] : [{height:'0px',opacity:0},{height:`${height}px`,opacity:1}], {duration:200,easing:'ease-out'});
      animation.onfinish = () => { if (closing) detail.open = false; content.style.removeProperty('overflow'); delete detail.dataset.animating; };
    }
    const arrow = event.target.closest('[data-lv-scroll]');
    if (arrow) {
      const track = document.getElementById(arrow.dataset.lvTarget);
      track?.scrollBy({ left: Number(arrow.dataset.lvScroll) * (track.firstElementChild?.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0) || track.clientWidth), behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'instant' : 'smooth' });
    }
  });
}
